import { Injectable, WritableSignal, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SigninManager } from 'sham-components/shared/auth';
import { SigninSubmitRequest } from 'sham-components/shared/models/auth';
import { AuthService } from './auth.service';
import { SigninApi } from './signin-api';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class CustomerSupportSigninManager extends SigninManager {
  private readonly customerSupportSigninApi = inject(SigninApi);
  private readonly authService = inject(AuthService);
  private readonly translate = inject(TranslateService);

  override async submitForm(
    payload: SigninSubmitRequest,
    submitError: WritableSignal<string | null>,
    loading: WritableSignal<boolean>,
  ) {
    try {
      const result = await firstValueFrom(
        this.customerSupportSigninApi.login(payload),
      );

      if (!result?.accessToken) {
        this.authService.logout();
        submitError.set(this.translate.instant('dashboardLogin.failed'));
        return;
      }

      if (
        !Array.isArray(result.user?.roles) ||
        !result.user.roles.includes('Accountant')
      ) {
        this.authService.logout();
        submitError.set(
          this.translate.instant(
            "You don't have enough permissions to access this dashboard.",
          ),
        );
        return;
      }

      this.authService.saveLogin(result, payload.rememberMe);
      this.authService.handleLoginRedirection(result.user);
    } catch (error: unknown) {
      this.authService.logout();
      const status =
        typeof error === 'object' && error && 'status' in error
          ? (error as { status?: number }).status
          : undefined;

      submitError.set(
        this.translate.instant(
          status === 401
            ? 'dashboardLogin.invalidCredentials'
            : 'dashboardLogin.error',
        ),
      );
    } finally {
      loading.set(false);
    }
  }
}
